import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftRight, Check, X, ChevronDown } from 'lucide-react';
import { trackPageView, trackEvent } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ComparisonPage = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([null, null]);
  const [open, setOpen] = useState(null); // which dropdown is open (0 or 1)

  useEffect(() => {
    trackPageView('/compare', 'Product Comparison Tool');
    fetch(`${BACKEND_URL}/api/products`)
      .then(r => r.json())
      .then(data => {
        const cementProducts = data.filter(p => p.category === 'Cement' || p.sub_categories);
        setProducts(cementProducts.length > 0 ? cementProducts : data);
      })
      .catch(() => {});
  }, []);

  const handleSelect = (idx, product) => {
    const next = [...selected];
    next[idx] = product;
    setSelected(next);
    setOpen(null);
    trackEvent('compare_select', 'comparison', { product: product.name, slot: idx });
  };

  const swap = () => setSelected([selected[1], selected[0]]);

  const a = selected[0];
  const b = selected[1];

  const specKeys = (() => {
    if (!a || !b) return [];
    const aSpecs = (a.specifications || []).map(s => s.split(':')[0].trim());
    const bSpecs = (b.specifications || []).map(s => s.split(':')[0].trim());
    const all = [...new Set([...aSpecs, ...bSpecs])];
    return all;
  })();

  const getSpecValue = (product, key) => {
    const spec = (product.specifications || []).find(s => s.startsWith(key));
    return spec ? spec.split(':').slice(1).join(':').trim() : '-';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4" data-testid="comparison-page">
      <SEOHead
        title="Product Comparison"
        description="Compare Avi Cement products side by side — specifications, use cases, benefits. Choose the right cement, adhesive, or block for your project."
        path="/compare"
      />
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto text-center mb-12"
      >
        <span className="text-[#E85A1C] text-xs font-semibold tracking-[0.2em] uppercase">
          Product Comparison
        </span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
          Compare <span className="text-[#F5A623]">Side by Side</span>
        </h1>
        <p className="text-gray-400 text-sm sm:text-base max-w-md mx-auto">
          Do products ko select karein aur unke specifications, grades, aur features ka comparison dekhein.
        </p>
      </motion.div>

      {/* Selector Row */}
      <div className="max-w-4xl mx-auto mb-10">
        <div className="flex items-center gap-3 md:gap-6">
          {[0, 1].map(idx => (
            <div key={idx} className="flex-1 relative">
              <button
                onClick={() => setOpen(open === idx ? null : idx)}
                data-testid={`compare-select-${idx}`}
                className={`w-full p-4 rounded-xl border text-left flex items-center justify-between transition-all ${
                  selected[idx]
                    ? 'bg-[#111113] border-[#E85A1C]/30 text-white'
                    : 'bg-[#111113] border-gray-800 text-gray-500'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {selected[idx] ? (
                    <>
                      <div
                        className="w-8 h-8 rounded-lg flex-shrink-0"
                        style={{ backgroundColor: `${selected[idx].color}20` }}
                      />
                      <span className="truncate font-medium text-sm">{selected[idx].name}</span>
                    </>
                  ) : (
                    <span className="text-sm">Product {idx + 1} select karein</span>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${open === idx ? 'rotate-180' : ''}`} />
              </button>

              {open === idx && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute z-50 top-full mt-2 w-full bg-[#1a1a1d] border border-gray-700 rounded-xl shadow-2xl max-h-60 overflow-y-auto"
                  data-testid={`compare-dropdown-${idx}`}
                >
                  {products.map(p => (
                    <button
                      key={p.id}
                      onClick={() => handleSelect(idx, p)}
                      disabled={selected[1 - idx]?.id === p.id}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-white/5 transition-colors ${
                        selected[1 - idx]?.id === p.id ? 'opacity-30 cursor-not-allowed' : 'text-white'
                      }`}
                    >
                      <div className="w-6 h-6 rounded flex-shrink-0" style={{ backgroundColor: `${p.color}20` }} />
                      {p.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>
          ))}

          <button
            onClick={swap}
            data-testid="compare-swap-btn"
            className="p-3 rounded-xl bg-[#111113] border border-gray-800 hover:border-[#E85A1C]/30 text-gray-400 hover:text-white transition-all flex-shrink-0"
            title="Swap products"
          >
            <ArrowLeftRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Comparison Table */}
      {a && b && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
          data-testid="comparison-table"
        >
          {/* Header */}
          <div className="grid grid-cols-3 gap-px bg-gray-800 rounded-t-2xl overflow-hidden">
            <div className="bg-[#111113] p-4">
              <span className="text-gray-500 text-xs font-mono uppercase">Specification</span>
            </div>
            <div className="bg-[#111113] p-4 text-center">
              <span className="text-sm font-semibold" style={{ color: a.color }}>{a.name}</span>
            </div>
            <div className="bg-[#111113] p-4 text-center">
              <span className="text-sm font-semibold" style={{ color: b.color }}>{b.name}</span>
            </div>
          </div>

          {/* Category */}
          <div className="grid grid-cols-3 gap-px bg-gray-800">
            <div className="bg-[#0e0e10] p-4"><span className="text-gray-400 text-sm">Category</span></div>
            <div className="bg-[#0e0e10] p-4 text-center"><span className="text-white text-sm">{a.category}</span></div>
            <div className="bg-[#0e0e10] p-4 text-center"><span className="text-white text-sm">{b.category}</span></div>
          </div>

          {/* Tagline */}
          <div className="grid grid-cols-3 gap-px bg-gray-800">
            <div className="bg-[#111113] p-4"><span className="text-gray-400 text-sm">Tagline</span></div>
            <div className="bg-[#111113] p-4 text-center"><span className="text-gray-300 text-sm italic">{a.tagline}</span></div>
            <div className="bg-[#111113] p-4 text-center"><span className="text-gray-300 text-sm italic">{b.tagline}</span></div>
          </div>

          {/* Grades */}
          {(a.sub_categories || b.sub_categories) && (
            <div className="grid grid-cols-3 gap-px bg-gray-800">
              <div className="bg-[#0e0e10] p-4"><span className="text-gray-400 text-sm">Available Grades</span></div>
              <div className="bg-[#0e0e10] p-4 text-center">
                {a.sub_categories ? (
                  <div className="flex flex-wrap gap-1 justify-center">
                    {a.sub_categories.map((sc, i) => (
                      <span key={i} className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: `${a.color}20`, color: a.color }}>
                        {sc.grade}
                      </span>
                    ))}
                  </div>
                ) : <span className="text-gray-500 text-sm">Single grade</span>}
              </div>
              <div className="bg-[#0e0e10] p-4 text-center">
                {b.sub_categories ? (
                  <div className="flex flex-wrap gap-1 justify-center">
                    {b.sub_categories.map((sc, i) => (
                      <span key={i} className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: `${b.color}20`, color: b.color }}>
                        {sc.grade}
                      </span>
                    ))}
                  </div>
                ) : <span className="text-gray-500 text-sm">Single grade</span>}
              </div>
            </div>
          )}

          {/* Specifications */}
          {specKeys.map((key, i) => (
            <div key={key} className={`grid grid-cols-3 gap-px bg-gray-800`}>
              <div className={`${i % 2 === 0 ? 'bg-[#111113]' : 'bg-[#0e0e10]'} p-4`}>
                <span className="text-gray-400 text-sm">{key}</span>
              </div>
              <div className={`${i % 2 === 0 ? 'bg-[#111113]' : 'bg-[#0e0e10]'} p-4 text-center`}>
                <span className="text-white text-sm">{getSpecValue(a, key)}</span>
              </div>
              <div className={`${i % 2 === 0 ? 'bg-[#111113]' : 'bg-[#0e0e10]'} p-4 text-center`}>
                <span className="text-white text-sm">{getSpecValue(b, key)}</span>
              </div>
            </div>
          ))}

          {/* Use Cases */}
          <div className="grid grid-cols-3 gap-px bg-gray-800">
            <div className="bg-[#111113] p-4"><span className="text-gray-400 text-sm">Best Use Cases</span></div>
            <div className="bg-[#111113] p-4">
              <ul className="space-y-1">
                {(a.use_cases || []).slice(0, 5).map((uc, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-gray-300">
                    <Check className="w-3 h-3 flex-shrink-0" style={{ color: a.color }} /> {uc}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#111113] p-4">
              <ul className="space-y-1">
                {(b.use_cases || []).slice(0, 5).map((uc, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-gray-300">
                    <Check className="w-3 h-3 flex-shrink-0" style={{ color: b.color }} /> {uc}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-3 gap-px bg-gray-800 rounded-b-2xl overflow-hidden">
            <div className="bg-[#0e0e10] p-4"><span className="text-gray-400 text-sm">Key Benefits</span></div>
            <div className="bg-[#0e0e10] p-4">
              <ul className="space-y-1">
                {(a.benefits || []).slice(0, 5).map((b2, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-300">
                    <Check className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: a.color }} /> {b2}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#0e0e10] p-4">
              <ul className="space-y-1">
                {(b.benefits || []).slice(0, 5).map((b2, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-300">
                    <Check className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: b.color }} /> {b2}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {(!a || !b) && (
        <div className="max-w-4xl mx-auto text-center py-16 bg-[#111113] rounded-2xl border border-gray-800">
          <ArrowLeftRight className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-white text-lg font-medium mb-2" data-testid="comparison-empty-state">
            Dono Products Select Karein
          </h3>
          <p className="text-gray-500 text-sm">
            Upar se do products choose karein — unka side-by-side comparison yahan dikhega.
          </p>
        </div>
      )}
    </div>
  );
};

export default ComparisonPage;
